import {
  AcceptPaymentWithEsaMembershipCreditMutation,
  AcceptPaymentWithEsaMembershipCreditMutationVariables,
} from "@outschool/gql-frontend-generated";
import {
  ApolloQueryResult,
  gql,
  OperationVariables,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import * as React from "react";

import {
  AcceptPaymentWithStoredCreditMutation,
  AcceptPaymentWithStoredCreditMutationVariables,
  AcceptPaymentWithSubscriptionCreditMutation,
  AcceptPaymentWithSubscriptionCreditMutationVariables,
  CreditAccountFragmentFragment,
  CreditAccountQuery,
  CurrencyCode,
} from "../../shared/generated/graphql";

export const creditAccountFragment = gql`
  fragment CreditAccountFragment on CreditAccount {
    uid
    openToBuy
    balance
    updatedAt
    status
  }
`;

export const creditAccountQuery = gql`
  query CreditAccount {
    creditBalance {
      ...CreditAccountFragment
    }
  }
  ${creditAccountFragment}
`;

export const acceptPaymentWithStoredCreditMutation = gql`
  mutation AcceptPaymentWithStoredCredit(
    $orderSlugId: ID!
    $amountCents: Int!
    $currencyCode: CurrencyCode
    $exchangeRate: Float
  ) {
    acceptPaymentWithStoredCredit(
      orderSlugId: $orderSlugId
      amountCents: $amountCents
      currencyCode: $currencyCode
      exchangeRate: $exchangeRate
    )
  }
`;

export const acceptPaymentWithSubscriptionCreditMutation = gql`
  mutation AcceptPaymentWithSubscriptionCredit(
    $orderSlugId: ID!
    $amountCredits: Int!
    $currencyCode: CurrencyCode
    $exchangeRate: Float
  ) {
    acceptPaymentWithSubscriptionCredit(
      orderSlugId: $orderSlugId
      amountCredits: $amountCredits
      currencyCode: $currencyCode
      exchangeRate: $exchangeRate
    )
  }
`;

export const acceptPaymentWithEsaMembershipCreditMutation = gql`
  mutation AcceptPaymentWithEsaMembershipCredit(
    $orderSlugId: ID!
    $amountCredits: Int!
    $currencyCode: CurrencyCode
    $exchangeRate: Float
    $learnerUid: ID!
  ) {
    acceptPaymentWithEsaMembershipCredit(
      orderSlugId: $orderSlugId
      amountCredits: $amountCredits
      currencyCode: $currencyCode
      exchangeRate: $exchangeRate
      learnerUid: $learnerUid
    )
  }
`;

export function useCreditAccount(): {
  loading: boolean;
  creditAccount: CreditAccountFragmentFragment | null;
  error: Error | undefined;
  refetch: (
    variables?: OperationVariables,
  ) => Promise<ApolloQueryResult<CreditAccountQuery>>;
  acceptPaymentWithStoredCredit: (
    orderSlugId: string,
    amountCents: number,
    currencyCode: CurrencyCode,
    exchangeRate: number,
  ) => Promise<string>;
} {
  const [acceptPaymentWithStoredCredit] = useMutation<
    AcceptPaymentWithStoredCreditMutation,
    AcceptPaymentWithStoredCreditMutationVariables
  >(acceptPaymentWithStoredCreditMutation);

  const { loading, error, data, refetch } =
    useQueryWithPreviousData<CreditAccountQuery>(creditAccountQuery, {
      fetchPolicy: "network-only",
    });

  const acceptPaymentWithStoredCreditWrapped = React.useCallback(
    async (
      orderSlugId: string,
      amountCents: number,
      currencyCode: CurrencyCode,
      exchangeRate: number,
    ) => {
      const { data } = await acceptPaymentWithStoredCredit({
        variables: { orderSlugId, amountCents, currencyCode, exchangeRate },
      });
      if (!data?.acceptPaymentWithStoredCredit) {
        throw new Error("Data is not defined?");
      }
      return data.acceptPaymentWithStoredCredit;
    },
    [acceptPaymentWithStoredCredit],
  );

  return {
    loading,
    error,
    creditAccount: data?.creditBalance ?? null,
    refetch,
    acceptPaymentWithStoredCredit: acceptPaymentWithStoredCreditWrapped,
  };
}

export function useSubscriptionCreditAccount(): {
  loading: boolean;
  creditAccount: CreditAccountFragmentFragment | null;
  error: Error | undefined;
  refetch: (
    variables?: OperationVariables,
  ) => Promise<ApolloQueryResult<CreditAccountQuery>>;
  acceptPaymentWithSubscriptionCredit: (
    orderSlugId: string,
    amountCredits: number,
    currencyCode: CurrencyCode,
    exchangeRate: number,
  ) => Promise<boolean>;
} {
  const [acceptPaymentWithSubscriptionCredit] = useMutation<
    AcceptPaymentWithSubscriptionCreditMutation,
    AcceptPaymentWithSubscriptionCreditMutationVariables
  >(acceptPaymentWithSubscriptionCreditMutation);

  const { loading, error, data, refetch } =
    useQueryWithPreviousData<CreditAccountQuery>(creditAccountQuery, {
      fetchPolicy: "network-only",
    });

  const acceptPaymentWithSubscriptionCreditWrapped = React.useCallback(
    async (
      orderSlugId: string,
      amountCredits: number,
      currencyCode: CurrencyCode,
      exchangeRate: number,
    ) => {
      const { data } = await acceptPaymentWithSubscriptionCredit({
        variables: { orderSlugId, amountCredits, currencyCode, exchangeRate },
      });
      if (!data?.acceptPaymentWithSubscriptionCredit) {
        throw new Error("Data is not defined?");
      }
      return data.acceptPaymentWithSubscriptionCredit;
    },
    [acceptPaymentWithSubscriptionCredit],
  );

  return {
    loading,
    error,
    creditAccount: data?.creditBalance ?? null,
    refetch,
    acceptPaymentWithSubscriptionCredit:
      acceptPaymentWithSubscriptionCreditWrapped,
  };
}

export function useEsaMembershipCreditAccount(): {
  loading: boolean;
  creditAccount: CreditAccountFragmentFragment | null;
  error: Error | undefined;
  refetch: (
    variables?: OperationVariables,
  ) => Promise<ApolloQueryResult<CreditAccountQuery>>;
  acceptPaymentWithEsaMembershipCredit: (
    orderSlugId: string,
    amountCredits: number,
    currencyCode: CurrencyCode,
    exchangeRate: number,
    learnerUid: string,
  ) => Promise<boolean>;
} {
  const [acceptPaymentWithEsaMembershipCredit] = useMutation<
    AcceptPaymentWithEsaMembershipCreditMutation,
    AcceptPaymentWithEsaMembershipCreditMutationVariables
  >(acceptPaymentWithEsaMembershipCreditMutation);

  const { loading, error, data, refetch } =
    useQueryWithPreviousData<CreditAccountQuery>(creditAccountQuery, {
      fetchPolicy: "network-only",
    });

  const acceptPaymentWithEsaMembershipCreditWrapped = React.useCallback(
    async (
      orderSlugId: string,
      amountCredits: number,
      currencyCode: CurrencyCode,
      exchangeRate: number,
      learnerUid: string,
    ) => {
      const { data } = await acceptPaymentWithEsaMembershipCredit({
        variables: {
          orderSlugId,
          amountCredits,
          currencyCode,
          exchangeRate,
          learnerUid,
        },
      });
      if (!data?.acceptPaymentWithEsaMembershipCredit) {
        throw new Error("Data is not defined?");
      }
      return data.acceptPaymentWithEsaMembershipCredit;
    },
    [acceptPaymentWithEsaMembershipCredit],
  );

  return {
    loading,
    error,
    creditAccount: data?.creditBalance ?? null,
    refetch,
    acceptPaymentWithEsaMembershipCredit:
      acceptPaymentWithEsaMembershipCreditWrapped,
  };
}

export function useCreditAccountCached(skip = false): {
  loading: boolean;
  creditAccount: CreditAccountFragmentFragment | null;
  error: Error | undefined;
} {
  const { isLoggedIn } = useSession();
  const { loading, error, data } = useQueryWithPreviousData<CreditAccountQuery>(
    creditAccountQuery,
    { skip: !isLoggedIn || skip, context: { batchKey: "CreditAccounts" } },
  );
  return {
    loading,
    error,
    creditAccount: data?.creditBalance ?? null,
  };
}
